/* ══════════════════════════════════════════════════════════════
   Sector Heatmap — Treemap-Style Sector Performance
   ══════════════════════════════════════════════════════════════ */

import { fetchIndices } from '../services/market-data';
import { renderUnavailableBlock } from './data-state';

export interface HeatmapSector {
  name: string;
  change: number;
  marketCap: number; // weight
  stocks: Array<{ name: string; change: number }>;
}

// Sector index name → short name mapping
const SECTOR_MAP: Record<string, { name: string; weight: number }> = {
  'NIFTY BANK': { name: 'Banking', weight: 30 },
  'NIFTY IT': { name: 'IT', weight: 18 },
  'NIFTY ENERGY': { name: 'Energy', weight: 16 },
  'NIFTY AUTO': { name: 'Auto', weight: 12 },
  'NIFTY FMCG': { name: 'FMCG', weight: 10 },
  'NIFTY METAL': { name: 'Metals', weight: 6 },
  'NIFTY PHARMA': { name: 'Pharma', weight: 5 },
  'NIFTY REALTY': { name: 'Realty', weight: 3 },
  'NIFTY FINANCIAL SERVICES': { name: 'Financials', weight: 25 },
  'NIFTY MEDIA': { name: 'Media', weight: 2 },
  'NIFTY PSU BANK': { name: 'PSU Banks', weight: 8 },
  'NIFTY PRIVATE BANK': { name: 'Pvt Banks', weight: 15 },
};

function renderWithData(container: HTMLElement, data: HeatmapSector[]): void {
  const totalWeight = data.reduce((s, d) => s + d.marketCap, 0);

  const getColor = (change: number) => {
    if (change >= 2) return '#059669';
    if (change >= 1) return '#10b981';
    if (change >= 0) return '#6ee7b7';
    if (change >= -1) return '#fca5a5';
    if (change >= -2) return '#ef4444';
    return '#dc2626';
  };

  const blocks = data.map(s => {
    const widthPct = (s.marketCap / totalWeight * 100).toFixed(1);
    const bg = getColor(s.change);
    const stockChips = s.stocks.map(st => `<span class="hm-stock">${st.name} ${st.change >= 0 ? '+' : ''}${st.change}%</span>`).join('');
    return `
      <div class="heatmap-block" style="flex-basis:${widthPct}%; background:${bg}">
        <span class="hm-name">${s.name}</span>
        <span class="hm-change">${s.change >= 0 ? '+' : ''}${s.change}%</span>
        <div class="hm-stocks">${stockChips}</div>
      </div>`;
  }).join('');

  container.innerHTML = `
    <div class="sector-heatmap glass-card">
      <div class="card-header"><h3>🗺️ Sector Heatmap</h3></div>
      <div class="heatmap-grid">${blocks}</div>
    </div>`;
}

export function renderSectorHeatmap(container: HTMLElement, sectors?: HeatmapSector[]): void {
  if (sectors) {
    renderWithData(container, sectors);
  } else {
    container.innerHTML = `<div class="sector-heatmap glass-card" style="padding:2rem;text-align:center;">Loading sector heatmap...</div>`;
    fetchIndices().then(indices => {
      if (indices && indices.length > 0) {
        const mapped: HeatmapSector[] = [];
        for (const idx of indices) {
          const sectorInfo = SECTOR_MAP[idx.name?.toUpperCase()] || SECTOR_MAP[idx.symbol?.toUpperCase()];
          if (sectorInfo) {
            mapped.push({
              name: sectorInfo.name,
              change: idx.pChange || 0,
              marketCap: sectorInfo.weight,
              stocks: [], // individual stocks not in allIndices response
            });
          }
        }
        if (mapped.length > 0) {
          renderWithData(container, mapped);
        } else {
          container.innerHTML = renderUnavailableBlock('sector-heatmap glass-card', 'Live sector heatmap unavailable');
        }
      } else {
        container.innerHTML = renderUnavailableBlock('sector-heatmap glass-card', 'Live sector heatmap unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('sector-heatmap glass-card', 'Live sector heatmap unavailable');
    });
  }
}
